import Vue from 'vue'
import ApiService from '@/common/api.service'

export const state = {
  /*
   * vehicleSelectionSets is a "grouping" of multiple vehicles that are
   * selected. For example, one might wish to have a "global" set of
   * selected vehicles that is persistent across all screens or one
   * might want to create a set of selected vehicles for a particular
   * individual/group of widgets.
   */
  vehicleSelectionSets: {},
  queryByClient: null,
  queryByGroups: []
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @param state Object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns the sets of selected vehicles.
   *
   * @param state Object
   *
   * @return Object
   */
  vehicleSelectionSets (state) {
    return state.vehicleSelectionSets
  },

  /**
   * Returns either a client object or null depending on whether
   * queries should be made by client.
   *
   * @param state
   *
   * @return {Object|null}
   */
  getClient (state) {
    return state.queryByClient
  },

  /**
   * Returns either an array of vehicle group objects or empty array.
   *
   * @param state
   *
   * @return {Object|null}
   */
  getGroups (state) {
    return state.queryByGroups
  }
}

export const actions = {
  /**
   * Find vehicles assigned to a specific group
   *
   * @param context
   * @param set
   * @param group_ids Array
   * @returns {*}
   */
  byGroupsWithUnits (context, { set, group_ids }) {
    return ApiService
      .get(`vehicles/by-group/ids/with-units`, { group_ids })
      .then((response) => {
        context.commit('addSelectedVehicles', { set: set, vehicles: response.data.data })
      })
  },

  /**
   * Query/find one or more vehicles.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    const queryByClient = context.getters['getClient']

    if (queryByClient !== null) {
      return ApiService
        .get(`vehicles/by-client/${queryByClient.id}`, params)
        .then((response) => {
          return response
        })
    } else {
      return ApiService
        .get(`vehicles`, params)
        .then((response) => {
          return response
        })
    }
  },

  /**
   * Query/find one or more vehicles that has units assigned.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryWithUnits (context, params) {
    const queryByClient = context.getters['getClient']
    const queryByGroups = context.getters['getGroups']

    if (queryByClient !== null) {
      return ApiService
        .get(`vehicles/by-client/${queryByClient.id}/with-units`, params)
        .then((response) => {
          return response
        })
    } else if (queryByGroups.length > 0) {
      let group_ids = queryByGroups.map((group) => {
        return group.id
      })
      return ApiService
        .get(`vehicles/by-group/ids/with-units`, { group_ids, params })
        .then((response) => {
          return response
        })
    } else {
      return ApiService
        .get(`vehicles/with-units`, params)
        .then((response) => {
          return response
        })
    }
  },

  /**
   * Query/find recently monitored vehicles.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryRecentlyMonitored (context, params) {
    return ApiService
      .get(`users/recent/monitoring/vehicles`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Query/find vehicles based on their id's.
   *
   * @param context Object
   * @param vehicleIds Array of vehicle id's
   *
   * @return Promise
   */
  queryByVehicleIds (context, vehicleIds) {
    console.log('DEBUG queryByVehicleIds')
    return ApiService
      .get(`vehicles/with-units`, { id: vehicleIds.join(';') })
  }
}

export const mutations = {
  /**
   * Add a map or array of object vehicles that have been selected within
   * the specified set.
   *
   * @param state    Object
   * @param set      String
   * @param vehicles Array|Map
   */
  addSelectedVehicles (state, { set, vehicles }) {
    if (!state.vehicleSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.vehicleSelectionSets, set, {})
    }

    vehicles.forEach(vehicle => {
      Vue.set(state.vehicleSelectionSets[set], vehicle.id, vehicle)
    })
  },

  /**
   * Removes an array of vehicles from the set's list of selected vehicles.
   * The array must consist of the id's of the vehicles that are no
   * longer selected.
   *
   * @param state    Object
   * @param set      String
   * @param vehicles Array
   */
  removeSelectedVehicles (state, { set, vehicles }) {
    if (state.vehicleSelectionSets.hasOwnProperty(set)) {
      vehicles.forEach(vehicle => {
        Vue.delete(state.vehicleSelectionSets[set], vehicle.id)
      })
    }
  },

  /**
   * Clears all selected vehicles.
   *
   * @param state  Object
   */
  clearAllSelectedVehicles (state) {
    Vue.set(state, 'vehicleSelectionSets', {})
  },

  /**
   * Clears all selected vehicles within a specified set.
   *
   * @param state  Object
   * @param set      String
   */
  clearSelectedVehicles (state, set) {
    if (state.vehicleSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.vehicleSelectionSets, set, {})
    }
  },

  /**
   * Purge the module's state.
   *
   * @param state Object
   */
  purge (state) {
    state.vehicleSelectionSets = {}
    state.queryByClient = null
    state.queryByGroups = []
  },

  /**
   * Sets the client for which the vehicles should be queried.
   *
   * @param state
   *
   * @param client Object|null
   */
  setClient (state, client) {
    state.queryByClient = client
  },

  /**
   * Sets the Group for which the vehicles should be queried.
   *
   * @param state
   *
   * @param group Object|null
   */
  setGroup (state, group) {
    state.queryByGroups.push(group)
  },

  /**
   * Clears all selected groups
   *
   * @param state
   */
  clearGroups (state) {
    state.queryByGroups = []
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/assets/vehicles/findVehiclesWidgetModule.js